import React, { useState } from 'react';
import { Modal, Tag, Select, Space, Typography, Card, Row, Col, Statistic, Button, Tooltip } from 'antd';
import { EyeOutlined, FileTextOutlined, CarOutlined } from '@ant-design/icons';
import { useGetCargoHistoryQuery } from '../../store/api/cargoApi';
import { useGetRouteByIdQuery } from '../../store/api/routesApi';
import { CargoHistoryEntry } from '../../types';
import { formatDate } from '../../utils/dateUtils';
import RouteDetailsModal from '../routes/RouteDetailsModal';
import { DataTable } from '../../ui';

const { Option } = Select;
const { Text } = Typography;

interface CargoHistoryModalProps {
  visible: boolean;
  onClose: () => void;
  cargoId: number;
  cargoName?: string;
}

const CargoHistoryModal: React.FC<CargoHistoryModalProps> = ({
  visible,
  onClose,
  cargoId,
  cargoName
}) => {
  const [filterType, setFilterType] = useState<string>('all');
  const [selectedRouteId, setSelectedRouteId] = useState<number | null>(null);
  const [routeDetailsVisible, setRouteDetailsVisible] = useState(false);
  
  const { data: historyData, isLoading, error } = useGetCargoHistoryQuery(
    { cargoId },
    { skip: !visible || !cargoId }
  );

  // Получаем данные маршрута для отображения в модальном окне
  const { data: routeData } = useGetRouteByIdQuery(
    selectedRouteId!,
    { skip: !selectedRouteId }
  );

  const history = historyData?.data || [];

  // Фильтрация по типу изменений
  const filteredHistory = filterType === 'all' 
    ? history 
    : history.filter(entry => entry.actionType === filterType);

  // Статистика
  const totalChanges = history.length;
  const statusChanges = history.filter(entry => entry.actionType === 'status_change').length;
  const locationChanges = history.filter(entry => entry.actionType === 'location_change').length;
  const otherChanges = totalChanges - statusChanges - locationChanges;

  // Функция для определения связанных сущностей
  const getRelatedEntity = (entry: CargoHistoryEntry) => {
    const description = entry.description?.toLowerCase() || '';
    
    // Проверяем наличие ID маршрута в oldValue или newValue
    if (entry.oldValue?.currentRouteId || entry.newValue?.currentRouteId) {
      const routeId = entry.newValue?.currentRouteId || entry.oldValue?.currentRouteId;
      return {
        type: 'route',
        id: routeId,
        label: `Маршрут №${routeId}`,
        icon: <CarOutlined />
      };
    }
    
    // Проверяем наличие ID заявки
    if (entry.oldValue?.applicationId || entry.newValue?.applicationId) {
      const applicationId = entry.newValue?.applicationId || entry.oldValue?.applicationId;
      return {
        type: 'application',
        id: applicationId,
        label: `Заявка №${applicationId}`,
        icon: <FileTextOutlined />
      };
    }
    
    // Проверяем по тексту описания
    if (description.includes('маршрут')) {
      const routeMatch = description.match(/маршрут №(\d+)/);
      if (routeMatch) {
        return {
          type: 'route',
          id: parseInt(routeMatch[1]),
          label: `Маршрут №${routeMatch[1]}`,
          icon: <CarOutlined />
        };
      }
    }
    
    if (description.includes('заявк')) {
      return {
        type: 'application',
        id: null,
        label: 'Просмотр заявки',
        icon: <FileTextOutlined />
      };
    }
    
    return null;
  };

  // Функция для форматирования значений изменений
  const formatChangeValue = (key: string, value: any) => {
    // Перевод названий полей на русский
    const fieldLabels: { [key: string]: string } = {
      'status': 'Статус',
      'currentRouteId': 'Текущий маршрут',
      'currentWarehouseId': 'Текущий склад',
      'applicationId': 'Заявка',
      'location': 'Местоположение',
      'routeId': 'Маршрут',
      'warehouseId': 'Склад'
    };

    const fieldLabel = fieldLabels[key] || key;

    switch (key) {
      case 'status':
        const statusLabels: { [key: string]: string } = {
          'available': 'Доступен',
          'in_transit': 'В пути',
          'delivered': 'Доставлен',
          'reserved': 'Зарезервирован',
          'damaged': 'Поврежден',
          'lost': 'Потерян',
          'in_route': 'В маршруте',
          'on_warehouse': 'На складе'
        };
        return `${fieldLabel}: ${statusLabels[value] || value}`;
      
      case 'currentRouteId':
      case 'routeId':
        return `${fieldLabel}: ${value ? `№${value}` : 'Не назначен'}`;
      
      case 'currentWarehouseId':
      case 'warehouseId':
        return `${fieldLabel}: ${value ? `№${value}` : 'Не назначен'}`;
      
      case 'applicationId':
        return `${fieldLabel}: ${value ? `№${value}` : 'Не назначена'}`;
      
      default:
        return `${fieldLabel}: ${String(value)}`;
    }
  };

  // Попытаться извлечь реальную "дату события" из записи истории
  const getEventDate = (entry: CargoHistoryEntry) => {
    // приоритетные поля, которые могут содержать фактическую дату
    const candidates = [
      entry.eventDate,
      // возможные альтернативные названия
      (entry as any).eventAt,
      entry.newValue?.actualLoadingDate,
      entry.newValue?.actualUnloadingDate,
      entry.newValue?.loadingDate,
      entry.newValue?.unloadingDate,
      entry.newValue?.date,
      entry.oldValue?.actualLoadingDate,
      entry.oldValue?.actualUnloadingDate,
      entry.routeInfo?.actualLoadingDate,
      entry.routeInfo?.actualUnloadingDate
    ];

    for (const c of candidates) {
      if (c) return String(c);
    }
    return null;
  };

  // Обработчик для кнопки "Подробнее"
  const handleViewDetails = (entry: CargoHistoryEntry) => {
    const relatedEntity = getRelatedEntity(entry);
    
    if (relatedEntity) {
      if (relatedEntity.type === 'route' && relatedEntity.id) {
        // Открываем модальное окно с деталями маршрута
        setSelectedRouteId(relatedEntity.id);
        setRouteDetailsVisible(true);
      } else if (relatedEntity.type === 'application' && relatedEntity.id) {
        // Открыть конкретную заявку в новой вкладке
        window.open(`/applications/${relatedEntity.id}`, '_blank');
      } else if (relatedEntity.type === 'application') {
        // Открыть общую страницу заявок
        window.open(`/applications`, '_blank');
      }
    }
  };

  // Обработчик закрытия модального окна с деталями маршрута
  const handleRouteDetailsClose = () => {
    setRouteDetailsVisible(false);
    setSelectedRouteId(null);
  };

  const columns = [
    {
      title: 'Дата события',
      dataIndex: 'eventDate',
      key: 'eventDate',
      width: 140,
      render: (_: any, record: CargoHistoryEntry) => {
        const d = getEventDate(record);
        return d ? formatDate(d) : <Text type="secondary">—</Text>;
      }
    },
    {
      title: 'Дата операции',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (date: string) => formatDate(date)
    },
    {
      title: 'Тип изменения',
      dataIndex: 'actionType',
      key: 'actionType',
      width: 160,
      render: (type: string) => {
        const typeLabels: { [key: string]: string } = {
          'status_change': 'Изменение статуса',
          'location_change': 'Изменение местоположения',
          'creation': 'Создание',
          'update': 'Обновление',
          'deletion': 'Удаление'
        };
        
        const colorMap: { [key: string]: string } = {
          'status_change': 'blue',
          'location_change': 'green',
          'creation': 'green',
          'update': 'orange',
          'deletion': 'red'
        };

        return (
          <Tag color={colorMap[type] || 'default'}>
            {typeLabels[type] || type}
          </Tag>
        );
      }
    },
    {
      title: 'Маршрут',
      key: 'route',
      width: 200,
      render: (_: any, record: CargoHistoryEntry) => {
        if (!record.routeInfo) return '—';
        
        const routeText = `${record.routeInfo.loadingAddress || '—'} → ${record.routeInfo.unloadingAddress || '—'}`;
        return (
          <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
            <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
              #{record.routeInfo.id}
            </div>
            <div style={{ color: 'var(--text)' }}>
              {routeText.length > 25 ? (
                <>
                  <div>{routeText.substring(0, 25)}</div>
                  <div style={{ color: 'var(--text-secondary)' }}>{routeText.substring(25)}</div>
                </>
              ) : (
                routeText
              )}
            </div>
          </div>
        );
      }
    },
    {
      title: 'Описание',
      dataIndex: 'description',
      key: 'description',
      width: 250,
      render: (description: string, record: CargoHistoryEntry) => (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text style={{ fontSize: '14px', flex: 1, wordBreak: 'break-word' }}>
            {description}
          </Text>
          {getRelatedEntity(record) && (
            <Tooltip title="Подробнее">
              <Button
                type="link"
                size="small"
                icon={<EyeOutlined />}
                onClick={() => handleViewDetails(record)}
                style={{ padding: 0, height: 'auto' }}
              />
            </Tooltip>
          )}
        </div>
      )
    },
    {
      title: 'Фактические даты',
      key: 'actualDates',
      width: 180,
      render: (_: any, record: CargoHistoryEntry) => {
        if (!record.routeInfo) return '—';
        
        return (
          <div style={{ fontSize: '11px', lineHeight: '1.3' }}>
            {record.routeInfo.actualLoadingDate && (
              <div>
                <span style={{ color: '#52c41a' }}>Погрузка:</span> {formatDate(record.routeInfo.actualLoadingDate)}
              </div>
            )}
            {record.routeInfo.actualUnloadingDate && (
              <div>
                <span style={{ color: '#fa8c16' }}>Выгрузка:</span> {formatDate(record.routeInfo.actualUnloadingDate)}
              </div>
            )}
            {!record.routeInfo.actualLoadingDate && !record.routeInfo.actualUnloadingDate && (
              <div style={{ color: '#999' }}>Нет данных</div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Изменения',
      key: 'changes',
      width: 250,
      render: (value: any, record: CargoHistoryEntry) => {
        if (!record.oldValue && !record.newValue) {
          return <Text type="secondary">Нет данных</Text>;
        }

        const oldValue = record.oldValue;
        const newValue = record.newValue;

        return (
          <div style={{ fontSize: '12px' }}>
            {oldValue && (
              <div style={{ marginBottom: 4 }}>
                <Text type="secondary">Было:</Text>
                <div style={{ marginLeft: 8 }}>
                  {Object.entries(oldValue).map(([key, value]) => (
                    <div key={key}>
                      {formatChangeValue(key, value)}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {newValue && (
              <div>
                <Text type="success">Стало:</Text>
                <div style={{ marginLeft: 8 }}>
                  {Object.entries(newValue).map(([key, value]) => (
                    <div key={key}>
                      {formatChangeValue(key, value)}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      }
    },
    {
      title: 'Пользователь',
      dataIndex: 'userName',
      key: 'userName',
      width: 100,
      render: (userName: string) => userName || 'Система'
    }
  ];

  // getStatusColor was unused; removed to reduce lint noise

  return (
    <Modal
      title={`История груза: ${cargoName || `ID: ${cargoId}`}`}
      open={visible}
      onCancel={onClose}
      footer={null}
      width={1600}
      destroyOnHidden
    >
      {error && (
        <div style={{ marginBottom: 16, padding: 16, backgroundColor: '#fff2f0', border: '1px solid #ffccc7', borderRadius: 6 }}>
          <Text type="danger">Ошибка загрузки истории: {JSON.stringify(error)}</Text>
        </div>
      )}

      {/* Статистика */}
      <Card style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Statistic 
              title="Всего изменений" 
              value={totalChanges} 
              valueStyle={{ color: '#1890ff' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="Изменения статуса" 
              value={statusChanges} 
              valueStyle={{ color: '#52c41a' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="Изменения местоположения" 
              value={locationChanges} 
              valueStyle={{ color: '#faad14' }}
            />
          </Col>
          <Col span={6}>
            <Statistic 
              title="Другие изменения" 
              value={otherChanges} 
              valueStyle={{ color: '#722ed1' }}
            />
          </Col>
        </Row>
      </Card>

      {/* Фильтр */}
      <div style={{ marginBottom: 16 }}>
        <Space>
          <Text strong>Фильтр по типу:</Text>
          <Select
            value={filterType}
            onChange={setFilterType}
            style={{ width: 200 }}
          >
            <Option value="all">Все изменения</Option>
            <Option value="status_change">Изменения статуса</Option>
            <Option value="location_change">Изменения местоположения</Option>
            <Option value="creation">Создание</Option>
            <Option value="update">Обновления</Option>
            <Option value="deletion">Удаления</Option>
          </Select>
          <Text type="secondary">
            Показано: {filteredHistory.length} из {totalChanges}
          </Text>
        </Space>
      </div>

      {/* Таблица истории */}
      <DataTable
        columns={columns}
        dataSource={filteredHistory}
        rowKey="id"
        loading={isLoading}
        pagination={{
          pageSize: 10,
          showSizeChanger: true,
          showQuickJumper: true,
          showTotal: (total, range) => 
            `${range[0]}-${range[1]} из ${total} записей`
        }}
        scroll={{ y: 400 }}
      />

      {/* Модальное окно с деталями маршрута */}
      <RouteDetailsModal
        visible={routeDetailsVisible}
        route={routeData || null}
        onClose={handleRouteDetailsClose}
      />
    </Modal>
  );
};

export default CargoHistoryModal; 